import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Cookie, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  obterConsentimentoLocal,
  salvarConsentimento,
  abrirPreferenciasCookies,
  type CategoriasConsentimentoCookies,
  type OrigemConsentimentoCookies,
} from '@/lib/cookieConsent';
import { toast } from 'sonner';

export { abrirPreferenciasCookies };

export default function CookieConsentBanner() {
  const [visivel, setVisivel] = useState(false);
  const [modalAberto, setModalAberto] = useState(false);
  const [origemAtual, setOrigemAtual] = useState<OrigemConsentimentoCookies>('banner');
  const [salvando, setSalvando] = useState(false);

  // Categorias para customização
  const [medicao, setMedicao] = useState(false);
  const [publicidade, setPublicidade] = useState(false);

  useEffect(() => {
    // 1. Confere se já existe consentimento válido na versão atual
    const atual = obterConsentimentoLocal();
    if (!atual) {
      setVisivel(true);
      setOrigemAtual('banner');
    } else {
      setMedicao(atual.categorias.medicao);
      setPublicidade(atual.categorias.publicidade);
    }

    // 2. Ouve disparos para abrir o gerenciador (<= 2 cliques via rodapé, perfil, etc.)
    const handleAbrir = (e: Event) => {
      const customEvent = e as CustomEvent<{ origem?: OrigemConsentimentoCookies }>;
      const origem = customEvent.detail?.origem || 'rodape';
      setOrigemAtual(origem);

      const dados = obterConsentimentoLocal();
      if (dados) {
        setMedicao(dados.categorias.medicao);
        setPublicidade(dados.categorias.publicidade);
      }
      setModalAberto(true);
    };

    // 3. Atualiza estado se outro componente salvar
    const handleChanged = (e: Event) => {
      const customEvent = e as CustomEvent<{
        categorias: CategoriasConsentimentoCookies;
      }>;
      if (customEvent.detail?.categorias) {
        setMedicao(customEvent.detail.categorias.medicao);
        setPublicidade(customEvent.detail.categorias.publicidade);
        setVisivel(false);
      }
    };

    window.addEventListener('erplight:abrir-cookies', handleAbrir);
    window.addEventListener('erplight:consent-changed', handleChanged);

    return () => {
      window.removeEventListener('erplight:abrir-cookies', handleAbrir);
      window.removeEventListener('erplight:consent-changed', handleChanged);
    };
  }, []);

  const aplicarSalvar = async (
    cats: CategoriasConsentimentoCookies,
    origem: OrigemConsentimentoCookies
  ) => {
    setSalvando(true);
    try {
      await salvarConsentimento(cats, origem);
      setVisivel(false);
      setModalAberto(false);
      toast.success('Preferências de cookies salvas.');
    } catch {
      toast.error('Erro ao registrar consentimento.');
    } finally {
      setSalvando(false);
    }
  };

  const aceitarTodos = (origem: OrigemConsentimentoCookies = origemAtual) => {
    aplicarSalvar({ necessarios: true, medicao: true, publicidade: true }, origem);
  };

  const recusarTodos = (origem: OrigemConsentimentoCookies = origemAtual) => {
    aplicarSalvar({ necessarios: true, medicao: false, publicidade: false }, origem);
  };

  const salvarPersonalizado = () => {
    aplicarSalvar({ necessarios: true, medicao, publicidade }, origemAtual);
  };

  return (
    <>
      {/* Banner de Primeiro Acesso */}
      {visivel && !modalAberto && (
        <div
          role="dialog"
          aria-label="Consentimento de cookies e privacidade"
          aria-describedby="cookie-banner-descricao"
          className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6 bg-white/95 backdrop-blur-md border-t border-zinc-200 shadow-2xl transition-all animate-in fade-in slide-in-from-bottom duration-300"
        >
          <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div className="space-y-1.5 max-w-3xl">
              <div className="flex items-center gap-2 text-zinc-900 font-semibold text-base">
                <Cookie className="h-5 w-5 text-indigo-600" aria-hidden="true" />
                <span>Privacidade e Uso de Cookies</span>
              </div>
              <p id="cookie-banner-descricao" className="text-sm text-zinc-600 leading-relaxed">
                Utilizamos cookies essenciais para manter você conectada em segurança. Também usamos
                cookies de medição (Google Analytics 4 e CloudWatch RUM) e publicidade (Meta Pixel)
                apenas se você autorizar. Saiba mais em nossa{' '}
                <Link
                  to="/privacidade"
                  className="text-indigo-600 hover:text-indigo-700 underline font-medium"
                >
                  Política de Privacidade
                </Link>
                .
              </p>
            </div>

            {/* Ações com Equivalência Visual (ANPD 2022) */}
            <div className="flex flex-wrap items-center gap-2.5 w-full lg:w-auto shrink-0">
              <Button
                type="button"
                variant="outline"
                className="flex-1 sm:flex-none border-zinc-300 text-zinc-700 hover:bg-zinc-100 font-medium"
                onClick={() => {
                  setOrigemAtual('banner');
                  setModalAberto(true);
                }}
                disabled={salvando}
              >
                Personalizar
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="flex-1 sm:flex-none bg-zinc-200 hover:bg-zinc-300 text-zinc-800 font-medium"
                onClick={() => recusarTodos('banner')}
                disabled={salvando}
              >
                Recusar
              </Button>
              <Button
                type="button"
                className="flex-1 sm:flex-none bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
                onClick={() => aceitarTodos('banner')}
                disabled={salvando}
              >
                Aceitar todos
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Personalização e Revogação (<= 2 cliques) */}
      <Dialog open={modalAberto} onOpenChange={setModalAberto}>
        <DialogContent className="sm:max-w-lg p-6 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-2 text-zinc-900 font-bold text-lg">
              <ShieldCheck className="h-5 w-5 text-indigo-600" aria-hidden="true" />
              <DialogTitle>Preferências de Privacidade e Cookies</DialogTitle>
            </div>
            <DialogDescription className="text-sm text-zinc-500 mt-1">
              Gerencie como tratamos dados em seu dispositivo. Cookies necessários não podem ser
              desativados, pois garantem a operação e a autenticação do Gestão Leve.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 my-4">
            {/* Categoria 1: Necessários */}
            <div className="rounded-lg border border-zinc-200 p-3.5 bg-zinc-50/50">
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm text-zinc-900">
                      1. Cookies Necessários
                    </span>
                    <span className="text-[11px] font-medium bg-zinc-200 text-zinc-700 px-2 py-0.5 rounded-full">
                      Sempre Ativos
                    </span>
                  </div>
                  <p className="text-xs text-zinc-600 leading-relaxed">
                    Mantêm você autenticada no sistema, viabilizam a emissão de pedidos e protegem a
                    sessão via tokens seguros.
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={true}
                  disabled={true}
                  aria-label="Cookies necessários (obrigatórios)"
                  className="mt-1 h-5 w-5 rounded text-indigo-600 cursor-not-allowed opacity-75"
                />
              </div>
            </div>

            {/* Categoria 2: Medição */}
            <div className="rounded-lg border border-zinc-200 p-3.5 hover:border-zinc-300 transition-colors">
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <label
                    htmlFor="cookie-toggle-medicao"
                    className="font-semibold text-sm text-zinc-900 cursor-pointer block"
                  >
                    2. Cookies de Medição e Desempenho
                  </label>
                  <p className="text-xs text-zinc-600 leading-relaxed">
                    Ajudam a identificar falhas de JavaScript e entender quais funções são mais
                    usadas, sem identificar você diretamente (Google Analytics 4 e AWS CloudWatch
                    RUM).
                  </p>
                </div>
                <input
                  id="cookie-toggle-medicao"
                  type="checkbox"
                  checked={medicao}
                  onChange={(e) => setMedicao(e.target.checked)}
                  className="mt-1 h-5 w-5 rounded text-indigo-600 accent-indigo-600 cursor-pointer"
                />
              </div>
            </div>

            {/* Categoria 3: Publicidade */}
            <div className="rounded-lg border border-zinc-200 p-3.5 hover:border-zinc-300 transition-colors">
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <label
                    htmlFor="cookie-toggle-publicidade"
                    className="font-semibold text-sm text-zinc-900 cursor-pointer block"
                  >
                    3. Cookies de Publicidade e Conversão
                  </label>
                  <p className="text-xs text-zinc-600 leading-relaxed">
                    Permitem avaliar a eficácia de nossas campanhas de atração de novos membros nas
                    redes sociais (Meta Pixel e Meta Conversions API).
                  </p>
                </div>
                <input
                  id="cookie-toggle-publicidade"
                  type="checkbox"
                  checked={publicidade}
                  onChange={(e) => setPublicidade(e.target.checked)}
                  className="mt-1 h-5 w-5 rounded text-indigo-600 accent-indigo-600 cursor-pointer"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 p-3 bg-blue-50 text-blue-800 rounded-lg text-xs">
              <Info className="h-4 w-4 shrink-0 text-blue-600" />
              <span>
                Você pode revogar ou alterar seu consentimento a qualquer momento nesta tela ou pelo
                link no rodapé.
              </span>
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-2.5 pt-2 border-t border-zinc-200">
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto text-zinc-700 border-zinc-300"
              onClick={() => recusarTodos(origemAtual)}
              disabled={salvando}
            >
              Revogar opcionais
            </Button>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button
                type="button"
                variant="secondary"
                className="flex-1 sm:flex-none"
                onClick={() => aceitarTodos(origemAtual)}
                disabled={salvando}
              >
                Aceitar todos
              </Button>
              <Button
                type="button"
                className="flex-1 sm:flex-none bg-indigo-600 hover:bg-indigo-700 text-white"
                onClick={salvarPersonalizado}
                disabled={salvando}
              >
                Salvar preferências
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
